<template>
  <Teleport to="body">
    <Transition name="success-modal">
      <div
        v-if="modelValue"
        class="success-modal-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="success-modal-title"
        @click.self="close"
      >
        <div class="success-modal-panel">
          <button
            type="button"
            class="success-modal-close"
            aria-label="Close"
            @click="close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <h2 id="success-modal-title" class="success-modal-title">
            {{ title }}
          </h2>
          <p v-if="message" class="success-modal-message">
            {{ message }}
          </p>
          <div class="success-modal-actions">
            <UIButton
              variant="primary"
              rounded="8"
              padding="16px 32px"
              :text="closeButtonText"
              @click="close"
            />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
interface Props {
  /** Заголовок диалогу */
  title?: string
  /** Текст повідомлення (опційно) */
  message?: string
  /** Текст кнопки закриття */
  closeButtonText?: string
}

const props = withDefaults(defineProps<Props>(), {
  title: undefined,
  message: undefined,
  closeButtonText: undefined,
})

const modelValue = defineModel<boolean>({ default: false })
const { t } = useI18n()

const SHORT_TITLE_MAX_LENGTH = 50

const rawTitle = computed(() => props.title ?? t('index.contactForm.successModal.title'))
const rawMessage = computed(() => props.message ?? t('index.contactForm.successModal.message'))

/** Заголовок: лише короткий текст; довгий — переноситься в message */
const title = computed(() => {
  const tVal = String(rawTitle.value || '')
  if (tVal.length <= SHORT_TITLE_MAX_LENGTH) return tVal
  return t('index.contactForm.successModal.titleShort') || 'Дякуємо!'
})

const message = computed(() => {
  const tVal = String(rawTitle.value || '')
  const mVal = String(rawMessage.value || '')
  if (tVal.length > SHORT_TITLE_MAX_LENGTH) return tVal
  return mVal
})
const closeButtonText = computed(() => props.closeButtonText ?? t('index.contactForm.successModal.closeButton'))

function close() {
  modelValue.value = false
}
</script>

<style scoped>
.success-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.5);
  box-sizing: border-box;
}

.success-modal-panel {
  position: relative;
  width: 100%;
  max-width: 425px;
  padding: 32px 24px 24px;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  font-family: var(--font-inter);
}

.success-modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-grey);
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.2s, color 0.2s;
}

.success-modal-close:hover {
  background: var(--color-light-grey);
  color: var(--color-dark-blue);
}

.success-modal-title {
  margin: 0 0 12px;
  font-family: var(--font-inter);
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--color-dark-blue);
  padding-right: 24px;
}

.success-modal-message {
  margin: 0 0 24px;
  font-family: var(--font-inter);
  font-size: var(--font-size-body);
  font-weight: 400;
  line-height: var(--line-height-body);
  color: var(--color-dark-blue);
  padding-right: 24px;
}

.success-modal-actions {
  display: flex;
  justify-content: flex-end;
}

/* Transition */
.success-modal-enter-active,
.success-modal-leave-active {
  transition: opacity 0.2s ease;
}

.success-modal-enter-from,
.success-modal-leave-to {
  opacity: 0;
}

</style>
